import {
  Clock,
  CheckCircle2,
  BarChart3,
  Users,
  Settings,
  FileText,
  PoundSterling,
  Briefcase,
  TrendingUp,
  Bell,
  Download,
  Upload,
  Archive,
  Shield,
  Zap,
  BookOpen,
  HelpCircle,
  Home,
  ClipboardCheck,
  LayoutDashboard,
  AlertTriangle,
} from 'lucide-react';
import type { NavigationConfig } from '@/types/navigation';


/**
 * Navigation Configuration
 *
 * This is the single source of truth for the application's navigation structure.
 * Updated: 2024-01-19 - Added Missing Timesheets Report
 * To add new menu items:
 * 1. Add the item to the appropriate section
 * 2. Define the path (route)
 * 3. Add an icon from lucide-react
 * 4. Optionally add children for sub-menus
 * 5. Add badges for notifications/counts
 */
export const navigationConfig: NavigationConfig = {
  sections: [
    // Main Navigation Section
    {
      id: 'main',
      items: [
        {
          id: 'homepage',
          label: 'Homepage',
          icon: Home,
          path: '/',
          description: 'Overview and key metrics',
        },
        {
          id: 'timesheet',
          label: 'My Timesheet',
          icon: Clock,
          path: '/timesheet',
          description: 'Enter and manage your time',
        },
        {
          id: 'approvals',
          label: 'Approvals',
          icon: CheckCircle2,
          path: '/approvals',
          badge: 12, // Dynamic count from context
          badgeVariant: 'destructive',
          description: 'Review and approve timesheets',
        },
      ],
    },

    // Reports & Analytics Section
    {
      id: 'reports',
      title: 'Reports & Analytics',
      items: [
        {
          id: 'reports',
          label: 'Reports',
          icon: BarChart3,
          badge: '🆕', // NEW INDICATOR - Missing Timesheets added!
          children: [
            {
              id: 'reports-timesheet',
              label: 'Timesheet Reports',
              icon: FileText,
              path: '/reports/timesheet',
              description: 'Detailed timesheet analysis',
            },
            {
              id: 'reports-missing-timesheets',
              label: 'Missing Timesheets',
              icon: AlertTriangle,
              path: '/reports/missing-timesheets',
              description: 'Track and manage missing timesheet submissions',
            },
            {
              id: 'reports-utilisation',
              label: 'Utilisation',
              icon: TrendingUp,
              path: '/reports/utilisation',
              description: 'Team utilisation metrics',
            },

          ],
        },
        // Analytics nav entry removed — belongs in the PSA suite's cross-module Analytics/BI module.
      ],
    },

    // Management Section
    {
      id: 'management',
      title: 'Management',
      items: [
        {
          id: 'team',
          label: 'Team',
          icon: Users,
          children: [
            {
              id: 'team-members',
              label: 'Team Members',
              icon: Users,
              path: '/team/members',
              description: 'Manage team members',
            },

          ],
        },
        {
          id: 'projects',
          label: 'Projects',
          icon: Briefcase,
          children: [
            {
              id: 'projects-active',
              label: 'Active Projects',
              icon: Briefcase,
              path: '/projects/active',
              description: 'Currently active projects',
            },
            // Hidden: Archive and Budget menu items temporarily hidden per request
            // {
            //   id: 'projects-archive',
            //   label: 'Archived Projects',
            //   icon: Archive,
            //   path: '/projects/archive',
            //   description: 'Completed and archived projects',
            // },
            // {
            //   id: 'projects-budget',
            //   label: 'Budget Tracking',
            //   icon: PoundSterling,
            //   path: '/projects/budget',
            //   description: 'Project budget and forecasting',
            // },
          ],
        },
      ],
    },

    // Data & Integration Section
    {
      id: 'data',
      title: 'Data & Integration',
      items: [
        {
          id: 'integrations',
          label: 'Integrations',
          icon: Zap,
          path: '/integrations',
          description: 'Manage companies and their system integrations',
        },
        // Hidden: Import/Export menu item temporarily hidden per request
        // {
        //   id: 'import-export',
        //   label: 'Import/Export',
        //   icon: Download,
        //   children: [
        //     {
        //       id: 'import',
        //       label: 'Import Data',
        //       icon: Upload,
        //       path: '/data/import',
        //       description: 'Import timesheet data',
        //     },
        //     {
        //       id: 'export',
        //       label: 'Export Data',
        //       icon: Download,
        //       path: '/data/export',
        //       description: 'Export to CSV, Excel, PDF',
        //     },
        //   ],
        // },
      ],
    },

    // Administration Section
    {
      id: 'admin',
      title: 'Administration',
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: Settings,
          children: [
            {
              id: 'settings-general',
              label: 'General',
              icon: Settings,
              path: '/settings/general',
              description: 'General application settings',
            },
            {
              id: 'settings-roles-permissions',
              label: 'Roles & Permissions',
              icon: Shield,
              path: '/settings/roles-permissions',
              description: 'Manage user roles and permissions',
            },
            {
              id: 'settings-approvals',
              label: 'Approvals',
              icon: ClipboardCheck,
              path: '/settings/approvals',
              description: 'Configure approval workflows and rules',
            },
            // Hidden: Screen Data Modes menu item temporarily hidden per request
            // {
            //   id: 'settings-screens',
            //   label: 'Screen Data Modes',
            //   icon: LayoutDashboard,
            //   path: '/settings/screens',
            //   description: 'Configure data modes per screen',
            // },
            {
              id: 'settings-roles',
              label: 'Roles & Rates',
              icon: Briefcase,
              path: '/settings/roles',
              description: 'View roles and billing rates',
            },
            {
              id: 'settings-notifications',
              label: 'Notifications',
              icon: Bell,
              path: '/settings/notifications',
              description: 'Notification preferences',
            },
            {
              id: 'settings-security',
              label: 'Security',
              icon: Shield,
              path: '/settings/security',
              description: 'Security and permissions',
            },
          ],
        },
      ],
    },
  ],
};

/**
 * Helper function to get all menu items as a flat array
 * Useful for searching or finding specific items
 */
export const getAllMenuItems = (): Array<{ item: any; path: string[] }> => {
  const items: Array<{ item: any; path: string[] }> = [];
  
  const traverse = (menuItems: any[], path: string[] = []) => {
    menuItems.forEach((item) => {
      items.push({ item, path: [...path, item.id] });
      if (item.children) {
        traverse(item.children, [...path, item.id]);
      }
    });
  };

  navigationConfig.sections.forEach((section) => {
    traverse(section.items, [section.id]);
  });

  return items;
};

/**
 * Helper function to find a menu item by ID
 */
export const findMenuItemById = (id: string) => {
  const allItems = getAllMenuItems();
  return allItems.find((item) => item.item.id === id);
};

/**
 * Helper function to get breadcrumb trail for a given path
 * Always starts with Homepage, followed by the navigation hierarchy
 */
export const getBreadcrumbs = (currentPath: string) => {
  // If we're on the homepage, return empty array (no breadcrumbs needed)
  if (currentPath === '/') {
    return [];
  }

  const breadcrumbs: any[] = [];
  
  const findPath = (items: any[], path: string, trail: any[] = []) => {
    for (const item of items) {
      const newTrail = [...trail, item];
      
      if (item.path === path) {
        breadcrumbs.push(...newTrail);
        return true;
      }
      
      if (item.children && findPath(item.children, path, newTrail)) {
        return true;
      }
    }
    return false;
  };

  navigationConfig.sections.forEach((section) => {
    findPath(section.items, currentPath);
  });

  // If no exact match found, check for sub-paths (e.g., /settings/roles-permissions/edit/1)
  if (breadcrumbs.length === 0) {
    // Handle Edit Role and Create Role pages
    if (currentPath.startsWith('/settings/roles-permissions/edit/')) {
      navigationConfig.sections.forEach((section) => {
        findPath(section.items, '/settings/roles-permissions');
      });
      // Add "Edit Role" as the last breadcrumb
      if (breadcrumbs.length > 0) {
        breadcrumbs.push({
          id: 'edit-role',
          label: 'Edit Role',
          icon: Shield,
          path: currentPath,
        });
      }
    } else if (currentPath === '/settings/roles-permissions/create') {
      navigationConfig.sections.forEach((section) => {
        findPath(section.items, '/settings/roles-permissions');
      });
      // Add "Create Role" as the last breadcrumb
      if (breadcrumbs.length > 0) {
        breadcrumbs.push({
          id: 'create-role',
          label: 'Create Role',
          icon: Shield,
          path: currentPath,
        });
      }
    }
  }

  // Filter out parent items that don't have their own path (only show navigable items)
  // Keep the hierarchy: if a parent has children, only show it if it's part of the path
  const filteredBreadcrumbs = breadcrumbs.filter(item => {
    // Always include items with a path
    if (item.path) return true;
    // Include parent items only if they have children (to show hierarchy)
    return item.children && item.children.length > 0;
  });

  return filteredBreadcrumbs;
};

